/**
 * Wording for the competition's scoring period.
 *
 * The two competitions run as mirrored pairs: an EPL **gameweek** and a World
 * Cup **matchday** are the same concept under different names. Every surface
 * that names the period reads its copy from here so the axis is spelled one way
 * in one place, rather than re-deriving `isGameweek ? 'Gameweek' : 'Matchday'`
 * at each call site.
 */

export type PeriodAxis = 'gameweek' | 'matchday';

export interface PeriodLabels {
  axis: PeriodAxis;
  isGameweek: boolean;
  /** Singular, title case — "Gameweek". */
  long: string;
  /** Plural, title case — "Gameweeks". */
  plural: string;
  /** Abbreviation used in tight spaces — "GW". */
  short: string;
  /** "All Gameweeks" — the catch-all option in period filters. */
  all: string;
  /** Points-for-this-period column header — "GW PTS". */
  points: string;
}

const LABELS: Record<PeriodAxis, PeriodLabels> = {
  gameweek: {
    axis: 'gameweek',
    isGameweek: true,
    long: 'Gameweek',
    plural: 'Gameweeks',
    short: 'GW',
    all: 'All Gameweeks',
    points: 'GW PTS',
  },
  matchday: {
    axis: 'matchday',
    isGameweek: false,
    long: 'Matchday',
    plural: 'Matchdays',
    short: 'MD',
    all: 'All Matchdays',
    points: 'MD PTS',
  },
};

export const getPeriodLabels = (axis: PeriodAxis): PeriodLabels => LABELS[axis];

/**
 * Maps a tournament calendar's format onto the period axis.
 */
export const periodAxisFromFormat = (format: string | null | undefined): PeriodAxis =>
  format === 'knockout' ? 'matchday' : 'gameweek';

/**
 * The same rule as {@link periodAxisFromFormat}, expressed as the format literal
 * for the places that key off the competition rather than the period.
 */
export const normalizeCompetitionFormat = (
  format: string | null | undefined
): 'classic_season_long' | 'knockout' =>
  periodAxisFromFormat(format) === 'matchday' ? 'knockout' : 'classic_season_long';
